
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LocationIcon } from "../Icons/LocationIcon";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  selectHasMore,
  selectList,
  selectLoading,
  selectPagination,
} from "./redux/selector";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { actions, reducer, sliceKey } from "./redux/slice";
import TimeZoneSaga from "./redux/saga";
import { TimeZoneProps } from "./type";
import DropdownMenuTriggerComponent from "../Select/DropdownMenuTriggerComponent";
import DropdownMenuContentComponent from "./DropdownMenuContentComponent";

const Index: React.FC<TimeZoneProps> = ({
  onChange,
  value,
  id,
  label,
  error,
  placeholder,
  searchPlaceholder,
  isOnboarding,
}) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: TimeZoneSaga });

  const systemTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  const dispatch = useDispatch();
  const pagination = useSelector(selectPagination);
  const loading = useSelector(selectLoading);
  const hasMore = useSelector(selectHasMore);
  const list = useSelector(selectList);

  const [search, setSearch] = useState<string>("");

  const didInitSearch = useRef(false);
  const didInitSelect = useRef(false);

  /** Initial fetch */
  useEffect(() => {
    dispatch(actions.setPagination({ ...pagination, page: 1 }));
    dispatch(actions.doFetchList());
  }, []);

  useEffect(() => {
    if (isOnboarding && systemTimeZone && !didInitSearch.current) {
      setSearch(systemTimeZone);
      didInitSearch.current = true;
    }
  }, [isOnboarding, systemTimeZone]);

  useEffect(() => {
    if (!isOnboarding || didInitSelect.current || !systemTimeZone || !list?.length) return;

    const match = list.find(
      (opt) =>
        Array.isArray(opt.utc) && opt.utc.includes(systemTimeZone)
    );

    if (match?.value) {
      onChange?.(match.value);
      didInitSelect.current = true; // prevent future auto-selects
    }
  }, [isOnboarding, systemTimeZone, list, onChange]);

  useEffect(() => {
    dispatch(actions.setPagination({ ...pagination, page: 1, search }));
    dispatch(actions.doFetchList());
  }, [search]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      dispatch(actions.setPagination({ ...pagination, page: pagination.page + 1 }));
      dispatch(actions.doFetchList());
    }
  }, [dispatch, loading, hasMore, pagination]);

  const onValueSelect = useCallback(
    (val: string) => {
      onChange?.(val);
      setSearch(""); // optional: clear search after selection
    },
    [onChange]
  );

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setDropdownWidth(containerRef.current.offsetWidth);
    }
  }, []);

  return (
    <div ref={containerRef} className={`relative w-full`}>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <div className={`flex flex-col gap-[6px]`}>
          {label && (
            <label
              htmlFor={id}
              className={`text-xs leading-[18px] text-[#79808A] `}
            >
              {label}
            </label>
          )}
          <DropdownMenuTriggerComponent
            options={list}
            open={open}
            icon={<LocationIcon size={18} />}
            placeholder={placeholder}
            value={value}
          />
          <DropdownMenuContentComponent
            options={list}
            onChange={onValueSelect}
            searchPlaceholder={searchPlaceholder as string}
            setOpen={setOpen}
            dropdownWidth={dropdownWidth}
            value={value}
            hasMore={hasMore}
            loading={loading}
            onLoadMore={loadMore}
            search={search}
            setSearch={(val) => {
              setSearch(val);
            }}
          />
        </div>
      </DropdownMenu.Root>
    </div>
  );
};

export default Index;
